// The editor: owns the document store, history, camera, spatial index,
// renderer, selection and tools, and exposes the edit operations tools and UI use.
import type { Audio, BoardObject, Card, Connector, Endpoint, Frame, Op, ShapeKind, TextObj } from '../shared/types.ts';
import { AUDIO, AUDIO_EXT, type AudioType } from '../shared/audio.ts';
import { fromPlainText, plainText, truncateRichText } from '../shared/rich-text.ts';
import { keysBetween } from '../shared/fractional-index.ts';
import { newId } from '../shared/ids.ts';
import { opObjectId } from '../shared/ops.ts';
import { generateStressBoard, STICKY_COLORS } from '../shared/stress.ts';
import { validateObject } from '../shared/validate.ts';
import { Store } from './model/store.ts';
import { History } from './model/history.ts';
import { membershipOps, withFrameChildren } from './model/frames.ts';
import {
  columnParents, columnsIn, columnsRightOf, footerRect, layoutOps, members, neighbour, type HeightAt,
} from './model/columns.ts';
import { cardWidthIn, COLUMN, isColumn } from '../shared/columns.ts';
import { Camera } from './engine/camera.ts';
import { Layers } from './engine/layers.ts';
import { LOD_ZOOM, Renderer } from './engine/renderer.ts';
import { SpatialIndex } from './engine/spatial-index.ts';
import { hitObject } from './engine/hit-test.ts';
import { drawOverlay } from './engine/overlay.ts';
import type { Guide } from './engine/snap.ts';
import { TextCache } from './engine/text-cache.ts';
import { ConnectorGeometry, endpointPosition, hitPolyline } from './engine/connectors.ts';
import { ImageCache } from './engine/images.ts';
import { FRAME_TITLE_GAP_PX, FRAME_TITLE_PX } from './engine/draw/content.ts';
import { AUDIO_LAYOUT, audioPart, layoutScale } from './engine/draw/audio.ts';
import { AudioPlayer } from './audio/player.ts';
import { canvasMeasure, isTextBearing, layoutFor, linkAt, TextLayouts } from './text/placement.ts';
import { TextEditor, type EditOptions } from './text/text-editor.ts';
import type { Presence } from './sync/presence.ts';
import { aabb, objectMatrix, toLocal, union, type Rect } from './util/geometry.ts';
import { Emitter } from './util/emitter.ts';
import { h, isEditableTarget } from './util/dom.ts';
import type { Tool, ToolName } from './tools/types.ts';
import { SelectTool } from './tools/select.ts';
import { HandTool } from './tools/hand.ts';
import { StickyTool } from './tools/sticky.ts';
import { ShapeTool } from './tools/shape.ts';
import { TextTool } from './tools/text.ts';
import { PenTool } from './tools/pen.ts';
import { EraserTool } from './tools/eraser.ts';
import { ConnectorTool } from './tools/connector.ts';
import { FrameTool } from './tools/frame.ts';
import { CardTool } from './tools/card.ts';
import { findShortcut } from './keyboard.ts';
import { CARD_COLORS, cardPlainText } from '../shared/cards.ts';
import { boxAt } from './cards/face.ts';
import { People } from './cards/people.ts';
import { CardEditor, type CardEditOptions } from './cards/card-editor.ts';

type EditorEvents = {
  tool: ToolName;
  selection: undefined;
  /** The selected objects themselves changed (by anyone). */
  selectionContent: undefined;
  transforming: boolean;
  history: undefined;
  camera: undefined;
  /** UI actions triggered from the keyboard (the editor doesn't own that UI). */
  command: EditorCommand;
};

export type EditorCommand = 'shortcuts' | 'minimap';

const HIT_TOLERANCE = 4;           // screen px
const CONNECTOR_HIT = 6;           // screen px
const SNAP_CANDIDATE_LIMIT = 2000; // beyond this, snapping is skipped
const CLIPBOARD_KIND = 'atomic-lab/objects';
/** Our own clipboard format; text/plain carries readable text for other apps. */
export const CLIPBOARD_MIME = 'application/x-atomic-lab';
/** This tab's last copy, for pasting from a menu (see pasteFromClipboard). */
const OWN_COPY_KEY = 'atomic-lab-last-copy';

export type MenuPasteResult = 'pasted' | 'empty' | 'blocked';

export type ImageUploader = (files: File[], at: [number, number] | null) => void;
/** Adds audio clips (spec §19.4): files, or a recording with its title. */
export type AudioUploader = (clips: { blob: Blob; title: string }[], at: [number, number] | null) => void;

/** Audio files by type, or by name when the browser doesn't say. */
const isAudioFile = (f: File) => f.type.startsWith('audio/') || /\.(mp3|m4a|mp4|aac|wav|ogg|oga|opus|webm|flac)$/i.test(f.name);

export class Editor {
  readonly userId = 'local';
  readonly store = new Store();
  readonly index = new SpatialIndex();
  readonly camera = new Camera();
  /** Who can see the board: names for card avatars (loaded by the board page). */
  readonly people = new People();
  readonly layouts = new TextLayouts(canvasMeasure, () => this.people);
  /** A card's height at a width (columns set their cards' width, spec §13.6). */
  readonly heightAt: HeightAt = (card, w) => this.layouts.cardHeightAt(card, w);
  readonly textCache = new TextCache();
  readonly connectors = new ConnectorGeometry(this.store);
  readonly images = new ImageCache();
  /** Plays audio clips, one at a time (spec §19.2). */
  readonly audio = new AudioPlayer();
  readonly events = new Emitter<EditorEvents>();
  readonly history: History;
  readonly layers: Layers;
  readonly renderer: Renderer;
  readonly tools: Record<ToolName, Tool>;
  private readonly container: HTMLElement;
  private readonly textEditor: TextEditor;
  private readonly cardEditor: CardEditor;

  toolName: ToolName = 'select';
  shapeKind: ShapeKind = 'rect';
  stickyColor = STICKY_COLORS[0]!;
  penColor = '#1A1A1A';
  penWidth = 3;
  connectorRouting: Connector['routing'] = 'elbow';
  cardColor = CARD_COLORS[0]!;

  // Interactive-layer state, drawn by engine/overlay.ts.
  hovered: string | null = null;
  /** Object a connector end would attach to (highlighted while dragging). */
  attachTarget: string | null = null;
  marquee: Rect | null = null;
  guides: Guide[] = [];
  transforming = false;
  /** Last pointer position in world coords (paste target, presence). */
  pointer: [number, number] | null = null;

  /** Receives every local op that took effect (edits, undo/redo, generated content), for sync. */
  onLocalOps: ((ops: readonly Op[]) => void) | null = null;
  /** Other people's cursors and selections, drawn by the overlay. */
  presence: Presence | null = null;
  /** Sync details for the debug overlay. */
  syncInfo: (() => { seq: number; pending: number; status: string }) | null = null;
  /** Uploads and inserts image files (set by the board page, which knows the workspace). */
  imageUploader: ImageUploader | null = null;
  /** Uploads and inserts audio clips (set by the board page). */
  audioUploader: AudioUploader | null = null;
  /** Starts recording from the microphone (set by the board page where recording is possible). */
  recordAudio: (() => void) | null = null;
  /** Runs before pasted objects are added (e.g. make pasted images available in this workspace). */
  beforePaste: ((objs: BoardObject[]) => Promise<void>) | null = null;

  /** Viewers: can pan, zoom and select, but every edit path is a no-op. */
  readonly readOnly: boolean;
  /** A board kept only in this browser (spec §14.4): no images, nobody to assign. */
  readonly local: boolean;

  private _selection = new Set<string>();
  private cursorOverride: string | null = null;
  private readonly frameIds = new Set<string>();
  /** Objects edited in the open gesture; frame membership is updated for them when it ends. */
  private readonly touched = new Set<string>();

  constructor(container: HTMLElement, opts: { readOnly?: boolean; local?: boolean } = {}) {
    this.container = container;
    this.readOnly = !!opts.readOnly;
    this.local = !!opts.local;
    this.history = new History((ops) => this.applyLocal(ops));
    this.layers = new Layers(container);
    this.renderer = new Renderer({
      layers: this.layers,
      camera: this.camera,
      store: this.store,
      index: this.index,
      layouts: this.layouts,
      textCache: this.textCache,
      connectors: this.connectors,
      images: this.images,
      drawOverlay: (ctx) => drawOverlay(ctx, this),
      editable: !this.readOnly,
    });
    this.images.onChange = () => this.renderer.invalidate();
    this.audio.onChange = () => this.audioChanged();
    this.people.onChange = () => this.renderer.invalidate();
    this.layers.onResize = () => this.renderer.invalidate();
    this.camera.onChange = () => {
      this.renderer.invalidate();
      this.events.emit('camera', undefined);
    };
    this.store.subscribe((ids) => this.onStoreChange(ids));
    this.tools = {
      select: new SelectTool(this),
      hand: new HandTool(this),
      sticky: new StickyTool(this),
      shape: new ShapeTool(this),
      text: new TextTool(this),
      pen: new PenTool(this),
      eraser: new EraserTool(this),
      connector: new ConnectorTool(this),
      frame: new FrameTool(this),
      card: new CardTool(this),
    };
    this.textEditor = new TextEditor(this, container);
    this.cardEditor = new CardEditor(this);
    this.bindClipboard();
    this.bindDrop();
    this.refreshCursor();
  }

  get tool(): Tool { return this.tools[this.toolName]; }
  get selection(): ReadonlySet<string> { return this._selection; }
  /** Object open in a DOM editor (text, or a card), if any. */
  get editingId(): string | null { return this.textEditor.editingId ?? this.cardEditor.editingId; }

  // --- edits ---

  /**
   * Applies ops as a user edit (recorded for undo; one undo step unless inside
   * a gesture). The single choke point for read-only mode.
   */
  apply(ops: Op[]): void {
    if (this.readOnly) return;
    const standalone = !this.history.inGesture;
    if (standalone) this.history.begin();
    // Including the frames a delete takes a child from: a column closes the gap (spec §13.6).
    const effective = this.withDeleteEffects(ops);
    this.history.record(this.applyEffective(effective));
    for (const op of effective) this.touched.add(opObjectId(op));
    if (standalone) this.endGesture();
  }

  /**
   * Applies ops locally and hands the effective ones to sync; returns their
   * inverses. Deleting an object also detaches connectors from it and removes
   * it from its frame (spec §5.6), in the same step so undo restores both.
   */
  private applyLocal(ops: readonly Op[]): Op[] {
    return this.applyEffective(this.withDeleteEffects(ops));
  }

  private applyEffective(ops: readonly Op[]): Op[] {
    const { applied, inverses } = this.store.applyTracked(ops);
    if (applied.length) this.onLocalOps?.(applied);
    return inverses;
  }

  private withDeleteEffects(ops: readonly Op[]): readonly Op[] {
    let deleting: Set<string> | null = null;
    for (const op of ops) if (op.t === 'delete' && this.store.has(op.id)) (deleting ??= new Set()).add(op.id);
    if (!deleting) return ops;
    const extra: Op[] = [];
    const get = (id: string) => this.store.get(id);
    for (const cid of this.connectors.attachedTo(deleting)) {
      const c = this.store.get(cid);
      if (deleting.has(cid) || c?.type !== 'connector') continue;
      const props: Partial<Connector> = {};
      for (const end of ['from', 'to'] as const) {
        const ep = c[end];
        if ('objectId' in ep && deleting.has(ep.objectId)) props[end] = { point: endpointPosition(c, end, get) };
      }
      extra.push({ t: 'update', id: cid, props });
    }
    for (const fid of this.frameIds) {
      const f = this.store.get(fid) as Frame | undefined;
      if (!f || deleting.has(fid)) continue;
      const kept = f.children.filter((c) => !deleting!.has(c));
      if (kept.length !== f.children.length) extra.push({ t: 'update', id: fid, props: { children: kept } });
    }
    return extra.length ? [...extra, ...ops] : ops;
  }

  beginGesture(): void { this.history.begin(); }

  endGesture(): void {
    if (this.history.nesting === 1 && this.touched.size) {
      // Frame membership follows what was moved, created or resized.
      const frames = [...this.frameIds].map((id) => this.store.get(id) as Frame);
      const ops = membershipOps(this.store, this.index, frames, this.touched, (o) => this.boundsOf(o));
      const touched = [...this.touched, ...ops.map(opObjectId)];
      this.touched.clear();
      if (ops.length) this.history.record(this.applyLocal(ops));
      // Then columns re-stack whatever the gesture changed in them, in the same step (spec §13.6).
      const restack = this.restackOps(touched, 0.01, true);
      if (restack.length) this.history.record(this.applyLocal(restack));
    }
    this.history.end();
    if (!this.history.inGesture) {
      this.touched.clear();
      this.events.emit('history', undefined);
    }
  }

  cancelGesture(): void {
    this.touched.clear();
    this.history.cancel();
    this.events.emit('history', undefined);
  }

  undo(): void {
    if (this.readOnly || this.history.inGesture) return;
    this.selectAffected(this.history.undo());
    this.events.emit('history', undefined);
  }

  redo(): void {
    if (this.readOnly || this.history.inGesture) return;
    this.selectAffected(this.history.redo());
    this.events.emit('history', undefined);
  }

  /** After undo/redo, select whatever the step touched that still exists. */
  private selectAffected(ops: readonly Op[] | null): void {
    if (!ops) return;
    this.setSelection(ops.map(opObjectId));
  }

  deleteSelection(): void {
    if (!this._selection.size) return;
    this.apply([...this._selection].map((id) => ({ t: 'delete', id })));
  }

  /** Props that move an object by (dx, dy). Connectors move only their free ends. */
  moveProps(o: BoardObject, dx: number, dy: number): Partial<BoardObject> {
    if (o.type !== 'connector') return { x: o.x + dx, y: o.y + dy };
    const shift = (e: Endpoint): Endpoint => ('point' in e ? { point: [e.point[0] + dx, e.point[1] + dy] } : e);
    return { from: shift(o.from), to: shift(o.to), x: o.x + dx, y: o.y + dy };
  }

  nudge(dx: number, dy: number): void {
    const objs = withFrameChildren(this.store, this.selectedObjects());
    this.apply(objs.map((o) => ({ t: 'update', id: o.id, props: this.moveProps(o, dx, dy) })));
  }

  duplicate(): void {
    const clones = this.cloneObjects(withFrameChildren(this.store, this.selectedObjects()), 20, 20);
    if (!clones.length) return;
    this.apply(clones.map((obj) => ({ t: 'create', obj })));
    this.setSelection(clones.map((c) => c.id));
  }

  bringToFront(): void {
    const objs = this.selectedObjects();
    if (!objs.length) return;
    const zs = keysBetween(this.store.topZ(), null, objs.length);
    this.apply(objs.map((o, i) => ({ t: 'update', id: o.id, props: { z: zs[i]! } })));
  }

  sendToBack(): void {
    const objs = this.selectedObjects();
    if (!objs.length) return;
    const zs = keysBetween(null, this.store.bottomZ(), objs.length);
    this.apply(objs.map((o, i) => ({ t: 'update', id: o.id, props: { z: zs[i]! } })));
  }

  /** `n` ascending z keys above everything on the board. */
  zKeysOnTop(n: number): string[] {
    return keysBetween(this.store.topZ(), null, n);
  }

  /** Copies objects with new ids, offset, stacked on top in their original order. */
  cloneObjects(objs: readonly BoardObject[], dx: number, dy: number): BoardObject[] {
    const sorted = [...objs].sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0));
    const ids = new Map(sorted.map((o) => [o.id, newId()]));
    const zs = this.zKeysOnTop(sorted.length);
    return sorted.map((o, i) => remapRefs(
      { ...o, ...this.moveProps(o, dx, dy), id: ids.get(o.id)!, z: zs[i]!, createdBy: this.userId } as BoardObject,
      ids,
    ));
  }

  // --- text ---

  startTextEdit(id: string, opts?: EditOptions): void {
    if (this.readOnly) return;
    this.textEditor.start(id, opts);
    this.renderer.invalidateInteractive();
  }

  /** Commits any open text or card edit. */
  commitTextEdit(): void {
    this.textEditor.commit();
    this.cardEditor.close();
  }

  /** Double-click with the select tool: edit a frame's title, an object's text, or open a card. */
  handleDoubleClick(wx: number, wy: number, clientX: number, clientY: number): void {
    if (this.toolName !== 'select' || this.readOnly) return;
    const title = this.frameTitleAt(wx, wy);
    if (title) {
      this.setSelection([title.id]);
      this.editFrameTitle(title);
      return;
    }
    const hit = this.hitTest(wx, wy);
    if (hit?.type === 'audio') {
      this.setSelection([hit.id]);
      this.renameAudio(hit.id);
      return;
    }
    if (hit?.type === 'card') {
      // Two quick clicks on a tick box are two ticks, not "open the card".
      if (this.tickBoxAt(wx, wy)) return;
      this.setSelection([hit.id]);
      this.openCard(hit.id);
      return;
    }
    if (!hit || !isTextBearing(hit)) return;
    this.setSelection([hit.id]);
    this.startTextEdit(hit.id, { caret: { clientX, clientY } });
  }

  /** Text objects and cards grow and shrink vertically with their content. */
  fitTextHeights(ids: Iterable<string>): void {
    const ops = this.heightOps(ids, 0.01);
    if (ops.length) this.apply(ops);
  }

  private heightOps(ids: Iterable<string>, tolerance: number): Op[] {
    const ops: Op[] = [];
    for (const id of ids) {
      const o = this.store.get(id);
      const h = o?.type === 'text' ? layoutFor(o, o.text, canvasMeasure).height
        : o?.type === 'card' ? this.layouts.cardFace(o).height : null;
      if (o && h !== null && Math.abs(h - o.h) > tolerance) ops.push({ t: 'update', id, props: { h } });
    }
    return ops;
  }

  // --- cards ---

  /** Opens the card editor (spec §13.3). */
  openCard(id: string, opts?: CardEditOptions): void {
    if (this.readOnly || this.store.get(id)?.type !== 'card') return;
    this.textEditor.commit();
    this.cardEditor.open(id, opts);
    this.renderer.invalidateInteractive();
  }

  /** Card edits as one undo step, with the card's height following its content. */
  editCard(id: string, ops: Op[]): void {
    if (this.readOnly) return;
    const standalone = !this.history.inGesture;
    if (standalone) this.history.begin();
    this.apply(ops);
    this.fitTextHeights([id]);
    if (standalone) this.endGesture();
  }

  /** Ticks or unticks a checklist item: one op, one undo step. */
  toggleItem(cardId: string, itemId: string): void {
    const o = this.store.get(cardId);
    if (o?.type !== 'card' || !Object.hasOwn(o.items, itemId)) return;
    this.editCard(cardId, [{ t: 'item', id: cardId, itemId, props: { done: !o.items[itemId]!.done } }]);
  }

  /** The card tick box under a world point, if the topmost object there is a card. */
  tickBoxAt(wx: number, wy: number): { cardId: string; itemId: string } | null {
    if (this.camera.zoom < LOD_ZOOM) return null; // not drawn at low zoom
    const hit = this.hitTest(wx, wy);
    if (hit?.type !== 'card') return null;
    const [lx, ly] = toLocal(hit, wx, wy);
    const itemId = boxAt(this.layouts.cardFace(hit), lx, ly, 4 / this.camera.zoom);
    return itemId ? { cardId: hit.id, itemId } : null;
  }

  /**
   * Re-fits cards whose content changed under them (e.g. two people adding
   * items at once, each sending a height for what they saw). Only the author
   * of a card's latest op does this, so clients never fight over it; not an
   * undo step.
   */
  repairCardHeights(ids: Iterable<string>): void {
    if (this.readOnly) return;
    const ops = this.heightOps(ids, 0.5);
    if (ops.length) this.applyLocal(ops);
  }

  cardOf(id: string): Card | null {
    const o = this.store.get(id);
    return o?.type === 'card' ? o : null;
  }

  /** The link under a world point, if the topmost object there has one. */
  linkAt(wx: number, wy: number): string | null {
    const hit = this.hitTest(wx, wy);
    const placed = hit && this.layouts.get(hit);
    if (!hit || !placed) return null;
    const [lx, ly] = toLocal(hit, wx, wy);
    return linkAt(placed, lx, ly);
  }

  // --- frames ---

  /** A frame whose title label (above the frame) is at this world point. */
  frameTitleAt(wx: number, wy: number): Frame | null {
    const band = (FRAME_TITLE_PX + FRAME_TITLE_GAP_PX + 4) / this.camera.zoom;
    let best: Frame | null = null;
    for (const id of this.frameIds) {
      const f = this.store.get(id) as Frame;
      if (wx >= f.x && wx <= f.x + f.w && wy >= f.y - band && wy <= f.y &&
        (!best || this.store.rank(f.id) > this.store.rank(best.id))) best = f;
    }
    return best;
  }

  renameFrame(id: string): void {
    const f = this.store.get(id);
    if (f?.type === 'frame' && !this.readOnly) this.editFrameTitle(f);
  }

  private editFrameTitle(f: Frame): void {
    const cam = this.camera;
    const [sx, sy] = cam.worldToScreen(f.x, f.y);
    const input = h('input', { class: 'frame-title-edit', type: 'text', maxlength: '200', 'aria-label': 'Frame title' });
    input.value = f.title;
    Object.assign(input.style, { left: `${sx}px`, top: `${sy - FRAME_TITLE_PX - FRAME_TITLE_GAP_PX - 10}px`, width: `${Math.max(160, Math.min(f.w * cam.zoom, 480))}px` });
    this.container.append(input);
    let done = false;
    const finish = (commit: boolean) => {
      if (done) return;
      done = true;
      const title = input.value.trim().slice(0, 200);
      input.remove();
      if (commit && this.store.has(f.id) && title !== f.title) this.apply([{ t: 'update', id: f.id, props: { title } }]);
    };
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') finish(true);
      if (e.key === 'Escape') finish(false);
    });
    input.addEventListener('blur', () => finish(true));
    input.focus();
    input.select();
  }

  // --- columns (spec §13.6) ---

  /** Every column on the board. */
  columnList(): Frame[] {
    return columnsIn(this.store, this.frameIds);
  }

  /** Ops re-stacking the columns `ids` touch: a column itself, or a card in one. */
  private restackOps(ids: Iterable<string>, tolerance: number, allRows: boolean): Op[] {
    const cols = this.columnList();
    if (!cols.length) return [];
    const parent = columnParents(cols);
    const wanted = new Set<string>();
    for (const id of ids) {
      if (isColumn(this.store.get(id))) wanted.add(id);
      const p = parent.get(id);
      if (p) wanted.add(p);
    }
    if (!wanted.size) return [];
    return layoutOps(this.store, cols, this.heightAt, { columns: wanted, tolerance, allRows });
  }

  /** Re-stacks columns mid-gesture (e.g. while one is resized). */
  restackColumns(ids: Iterable<string>): void {
    const ops = this.restackOps(ids, 0.01, false);
    if (ops.length) this.apply(ops);
  }

  /**
   * Tidies columns two people changed at once (each laying them out for what
   * they saw). Only the author of a column's latest op does this, so clients
   * never fight over it; not an undo step.
   */
  repairColumns(ids: Iterable<string>): void {
    // Mid-gesture (e.g. dragging cards out of a column) the echo of our own ops would re-stack under the
    // pointer; the gesture's end re-stacks what it touched anyway.
    if (this.readOnly || this.history.inGesture) return;
    const cols = [...ids];
    // A column's children list is one value, so two drops at once can each leave out the other's card:
    // membership first follows where the cards are, then the column re-stacks.
    const frames = [...this.frameIds].map((id) => this.store.get(id) as Frame);
    const membership = membershipOps(this.store, this.index, frames, cols, (o) => this.boundsOf(o));
    if (membership.length) this.applyLocal(membership);
    const ops = this.restackOps(cols, 0.5, false);
    if (ops.length) this.applyLocal(ops);
  }

  /** The "+ Add card" button under a world point, if any (drawn when zoomed in enough to read). */
  columnFooterAt(wx: number, wy: number): Frame | null {
    if (this.readOnly || this.camera.zoom < LOD_ZOOM) return null;
    for (const c of this.columnList()) {
      const r = footerRect(c);
      if (wx >= r.x && wx <= r.x + r.w && wy >= r.y && wy <= r.y + r.h) return c;
    }
    return null;
  }

  /** "+ Add card": a new card at the bottom of a column, open in the card editor. */
  addCardToColumn(columnId: string): void {
    const col = this.store.get(columnId);
    if (this.readOnly || !isColumn(col)) return;
    const w = cardWidthIn(col.w);
    const obj: Card = {
      id: newId(), type: 'card', x: col.x + (col.w - w) / 2, y: col.y + col.h, w, h: 0, rotation: 0,
      z: this.zKeysOnTop(1)[0]!, createdBy: this.userId, title: '', color: this.cardColor, items: {}, assignees: [],
    };
    obj.h = this.layouts.cardFace(obj).height;
    // As with the card tool, creating it and the first edit are one undo step; an empty card is discarded.
    this.beginGesture();
    this.apply([{ t: 'create', obj }]);
    const order = [...members(this.store, col).map((c) => c.id), obj.id];
    this.apply(layoutOps(this.store, this.columnList(), this.heightAt, { columns: [columnId], order: new Map([[columnId, order]]) }));
    this.setSelection([obj.id]);
    this.openCard(obj.id, { gesture: true, isNew: true });
  }

  /** An empty column to the right of this one; the columns to its right in the row move along. */
  addColumnRight(columnId: string): void {
    const col = this.store.get(columnId);
    if (this.readOnly || !isColumn(col)) return;
    const shift = col.w + COLUMN.spacing;
    const right = withFrameChildren(this.store, columnsRightOf(this.columnList(), col));
    const frame: Frame = {
      id: newId(), type: 'frame', x: col.x + shift, y: col.y, w: col.w, h: col.h, rotation: 0,
      z: keysBetween(null, this.store.bottomZ(), 1)[0]!, createdBy: this.userId,
      title: 'New column', children: [], layout: 'column',
    };
    this.apply([...right.map((o): Op => ({ t: 'update', id: o.id, props: this.moveProps(o, shift, 0) })), { t: 'create', obj: frame }]);
    this.setSelection([frame.id]);
    this.renameFrame(frame.id);
  }

  /** Turns a frame into a column, stacking its cards, or back into a plain frame (cards stay put). */
  setColumn(frameId: string, on: boolean): void {
    const f = this.store.get(frameId);
    if (this.readOnly || f?.type !== 'frame' || isColumn(f) === on) return;
    if (!on) {
      this.apply([{ t: 'update', id: frameId, props: { layout: undefined } }]);
      return;
    }
    // Only cards stay in a column; anything else in it is set free.
    const cards = f.children.filter((id) => this.store.get(id)?.type === 'card');
    this.apply([{ t: 'update', id: frameId, props: { layout: 'column', ...(cards.length !== f.children.length ? { children: cards } : {}) } }]);
  }

  /**
   * Alt+arrows on cards in a column: up and down within it, or left and right
   * to the end of the neighbouring column in the row. False if the selection
   * isn't cards in one column.
   */
  moveCardsInColumn(dir: 'up' | 'down' | 'left' | 'right'): boolean {
    const cols = this.columnList();
    const parent = columnParents(cols);
    const picked = this.selectedObjects();
    const colId = picked.length ? parent.get(picked[0]!.id) : undefined;
    if (!colId || picked.some((o) => o.type !== 'card' || parent.get(o.id) !== colId)) return false;
    const col = this.store.get(colId) as Frame;
    const ids = new Set(picked.map((o) => o.id));
    const order = members(this.store, col).map((c) => c.id);
    const block = order.filter((id) => ids.has(id));
    const rest = order.filter((id) => !ids.has(id));
    const orders = new Map<string, string[]>();
    if (dir === 'up' || dir === 'down') {
      const before = order.slice(0, order.indexOf(block[0]!)).filter((id) => !ids.has(id)).length;
      const at = Math.max(0, Math.min(rest.length, before + (dir === 'up' ? -1 : 1)));
      orders.set(colId, [...rest.slice(0, at), ...block, ...rest.slice(at)]);
    } else {
      const target = neighbour(cols, col, dir === 'left' ? -1 : 1);
      if (!target) return true;
      orders.set(colId, rest);
      orders.set(target.id, [...members(this.store, target).map((c) => c.id), ...block]);
    }
    if ([...orders].every(([id, o]) => id === colId && o.join() === order.join())) return true;
    this.apply(layoutOps(this.store, cols, this.heightAt, { columns: orders.keys(), order: orders }));
    return true;
  }

  // --- queries ---

  /** Topmost object under a world point (optionally only those `accept` allows). */
  hitTest(wx: number, wy: number, accept: (o: BoardObject) => boolean = () => true): BoardObject | null {
    const zoom = this.camera.zoom;
    const t = HIT_TOLERANCE / zoom;
    let best: BoardObject | null = null;
    let bestRank = -1;
    const consider = (o: BoardObject) => {
      const rank = this.store.rank(o.id);
      if (rank <= bestRank || !accept(o)) return;
      const hit = o.type === 'connector'
        ? hitPolyline(this.connectors.shape(o).points, wx, wy, CONNECTOR_HIT / zoom)
        : hitObject(o, wx, wy, t);
      if (hit) {
        best = o;
        bestRank = rank;
      }
    };
    const pad = Math.max(t, CONNECTOR_HIT / zoom);
    for (const id of this.index.query({ x: wx - pad, y: wy - pad, w: 2 * pad, h: 2 * pad })) consider(this.store.get(id)!);
    // Frame titles sit above their frames, outside their bounds.
    const title = this.frameTitleAt(wx, wy);
    if (title && this.store.rank(title.id) > bestRank && accept(title)) best = title;
    return best;
  }

  /** What a connector end at this point would attach to. */
  attachableAt(wx: number, wy: number, exclude: ReadonlySet<string> = new Set()): BoardObject | null {
    return this.hitTest(wx, wy, (o) => o.type !== 'connector' && !exclude.has(o.id));
  }

  /** World bounds used for culling and selection (connectors: their derived path). */
  boundsOf(o: BoardObject): Rect {
    return o.type === 'connector' ? this.connectors.shape(o).bounds : aabb(o);
  }

  /** Ids plus the connectors attached to them (they must redraw together). */
  withDependents(ids: Iterable<string>): Set<string> {
    const out = new Set(ids);
    for (const c of this.connectors.attachedTo(out)) out.add(c);
    return out;
  }

  viewport(): Rect {
    return this.camera.viewport(this.layers.width, this.layers.height);
  }

  /** Bounds of visible objects not in `exclude`, for snapping. */
  snapCandidates(exclude: { has(id: string): boolean }): Rect[] {
    const ids = this.index.query(this.viewport());
    if (ids.length > SNAP_CANDIDATE_LIMIT) return [];
    const out: Rect[] = [];
    for (const id of ids) {
      if (exclude.has(id) || this.store.get(id)?.type === 'connector') continue;
      out.push(this.index.getBounds(id)!);
    }
    return out;
  }

  contentBounds(): Rect | null {
    return union([...this.store.values()].map((o) => this.boundsOf(o)));
  }

  // --- selection ---

  setSelection(ids: Iterable<string>): void {
    const next = new Set<string>();
    for (const id of ids) if (this.store.has(id)) next.add(id);
    const cur = this._selection;
    if (next.size === cur.size && [...next].every((id) => cur.has(id))) return;
    this._selection = next;
    this.renderer.invalidateInteractive();
    this.events.emit('selection', undefined);
  }

  selectAll(): void {
    this.setSelection(this.store.orderedIds());
  }

  /** Selected objects in paint order. */
  selectedObjects(): BoardObject[] {
    return [...this._selection]
      .map((id) => this.store.get(id)!)
      .sort((a, b) => this.store.rank(a.id) - this.store.rank(b.id));
  }

  setTransforming(on: boolean): void {
    if (on === this.transforming) return;
    this.transforming = on;
    this.events.emit('transforming', on);
  }

  // --- tools & cursor ---

  setTool(name: ToolName): void {
    if (name === this.toolName) return;
    if (this.readOnly && name !== 'select' && name !== 'hand') return;
    this.tool.onCancel?.();
    this.toolName = name;
    if (this.hovered || this.attachTarget) {
      this.hovered = this.attachTarget = null;
    }
    this.renderer.invalidateInteractive();
    this.refreshCursor();
    this.events.emit('tool', name);
  }

  setCursorOverride(cursor: string | null): void {
    this.cursorOverride = cursor;
    this.refreshCursor();
  }

  refreshCursor(): void {
    this.layers.interactiveCanvas.style.cursor = this.cursorOverride ?? this.tool.cursor;
  }

  // --- images and audio ---

  /** Opens the file picker and inserts the chosen images and audio at the viewport centre. */
  pickFiles(): void {
    if (this.readOnly || !this.imageUploader) return;
    const accept = 'image/png,image/jpeg,image/webp,image/gif,audio/*,.mp3,.m4a,.wav,.ogg,.opus,.webm,.flac';
    const input = h('input', { type: 'file', accept, multiple: '' });
    input.addEventListener('change', () => this.insertFiles([...(input.files ?? [])], null));
    input.click();
  }

  /** Images go to the image uploader, audio to the audio one; anything else is ignored. */
  private insertFiles(files: File[], at: [number, number] | null): void {
    const images = files.filter((f) => f.type.startsWith('image/'));
    const audio = files.filter((f) => !f.type.startsWith('image/') && isAudioFile(f));
    if (images.length) this.imageUploader?.(images, at);
    if (audio.length) this.audioUploader?.(audio.map((f) => ({ blob: f, title: f.name.replace(/\.[^.]+$/, '') })), at);
  }

  /**
   * The audio control under a point (spec §19.2): a clip's play button, or the waveform of
   * the clip that's loaded. Not below the level-of-detail zoom, where there's only the button.
   */
  audioControlAt(wx: number, wy: number): { o: Audio; part: 'button' } | { o: Audio; part: 'wave'; frac: number } | null {
    const o = this.hitTest(wx, wy);
    if (o?.type !== 'audio') return null;
    const [lx, ly] = toLocal(o, wx, wy);
    const [sx, sy] = layoutScale(o);
    const at = audioPart(lx / sx, ly / sy);
    if (!at || (at.part === 'wave' && (this.audio.id !== o.id || this.camera.zoom < LOD_ZOOM))) return null;
    return at.part === 'button' ? { o, part: 'button' } : { o, part: 'wave', frac: at.frac };
  }

  /** Plays, pauses or seeks if the pointer went down on an audio control; true if it did. Works with any tool. */
  audioClick(wx: number, wy: number): boolean {
    const c = this.audioControlAt(wx, wy);
    if (!c) return false;
    if (c.part === 'button') this.audio.toggle(c.o);
    else this.audio.seek(c.o, c.frac);
    return true;
  }

  /** Redraws for the player; while a clip plays, the progress moves every frame (interactive layer only). */
  private audioChanged(): void {
    this.renderer.invalidate();
    const tick = () => {
      if (!this.audio.playing) return;
      this.renderer.invalidateInteractive();
      requestAnimationFrame(tick);
    };
    if (this.audio.playing) requestAnimationFrame(tick);
  }

  /** Saves a clip's file, named after its title. */
  downloadAudio(id: string): void {
    const o = this.store.get(id);
    if (o?.type !== 'audio') return;
    const name = (o.title || 'Audio').replace(/[\\/:*?"<>|]+/g, ' ').trim().slice(0, 100) || 'Audio';
    const a = h('a', { href: this.audio.url(o), download: `${name}.${AUDIO_EXT[o.mime as AudioType] ?? 'audio'}` });
    document.body.append(a);
    a.click();
    a.remove();
  }

  /** Edits a clip's title in place. */
  renameAudio(id: string): void {
    const o = this.store.get(id);
    if (o?.type !== 'audio' || this.readOnly) return;
    const cam = this.camera;
    const [sx, sy] = layoutScale(o);
    const { title, right } = AUDIO_LAYOUT;
    // Unrotated, it sits over the title; rotated, by the clip's top-left corner.
    const [x, y] = cam.worldToScreen(o.x + (o.rotation ? 0 : title.x * sx), o.y + (o.rotation ? 0 : (title.baseline - 20) * sy));
    const input = h('input', { class: 'frame-title-edit', type: 'text', maxlength: String(AUDIO.titleLength), 'aria-label': 'Audio title' });
    input.value = o.title;
    Object.assign(input.style, { left: `${x}px`, top: `${y}px`, width: `${Math.max(160, (right - title.x) * sx * cam.zoom)}px` });
    this.container.append(input);
    let done = false;
    const finish = (commit: boolean) => {
      if (done) return;
      done = true;
      const value = input.value.trim().slice(0, AUDIO.titleLength);
      input.remove();
      const now = this.store.get(id);
      if (commit && now?.type === 'audio' && value !== now.title) this.apply([{ t: 'update', id, props: { title: value } }]);
    };
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') finish(true);
      if (e.key === 'Escape') finish(false);
    });
    input.addEventListener('blur', () => finish(true));
    input.focus();
    input.select();
  }

  private bindDrop(): void {
    const hasFiles = (e: DragEvent) => [...(e.dataTransfer?.types ?? [])].includes('Files');
    this.container.addEventListener('dragover', (e) => {
      if (!hasFiles(e) || this.readOnly) return;
      e.preventDefault();
      e.dataTransfer!.dropEffect = 'copy';
    });
    this.container.addEventListener('drop', (e) => {
      if (!hasFiles(e) || this.readOnly) return;
      e.preventDefault();
      const r = this.container.getBoundingClientRect();
      this.insertFiles([...(e.dataTransfer?.files ?? [])], this.camera.screenToWorld(e.clientX - r.left, e.clientY - r.top));
    });
  }

  // --- camera ---

  zoomBy(factor: number): void {
    this.camera.zoomAt(this.layers.width / 2, this.layers.height / 2, factor);
  }

  zoomTo(zoom: number): void {
    this.zoomBy(zoom / this.camera.zoom);
  }

  zoomToFit(): void {
    const b = this.contentBounds();
    if (b) this.camera.fit(b, this.layers.width, this.layers.height);
    else this.camera.set(-this.layers.width / 2, -this.layers.height / 2, 1);
  }

  /** Screen transform for an object (CSS matrix), e.g. for DOM overlays. */
  cssMatrix(o: BoardObject): string {
    const cam = this.camera;
    return `matrix(${objectMatrix(o, cam.zoom, -cam.x * cam.zoom, -cam.y * cam.zoom).join(',')})`;
  }

  // --- keyboard ---

  handleKeyDown(e: KeyboardEvent): void {
    // The active tool gets plain keys first (Delete, arrows, Enter, Esc mid-drag).
    if (!e.metaKey && !e.ctrlKey) {
      this.tool.onKeyDown?.(e);
      if (e.defaultPrevented) return;
    }
    // Copy/cut/paste aren't here: they arrive as native clipboard events.
    const shortcut = findShortcut(e);
    if (!shortcut?.run) return;
    e.preventDefault();
    shortcut.run(this);
  }

  setShape(kind: ShapeKind): void {
    this.shapeKind = kind;
    this.setTool('shape');
    this.events.emit('tool', 'shape');
  }

  command(c: EditorCommand): void {
    this.events.emit('command', c);
  }

  // --- loading ---

  /** Replaces the board's content without syncing or history (e.g. a fixture). */
  loadObjects(objects: readonly BoardObject[]): void {
    this.commitTextEdit();
    this.setSelection([]);
    this.store.replaceAll(objects);
    this.history.clear();
    this.events.emit('history', undefined);
  }

  // --- dev / testing ---

  /** Adds generated objects below existing content. Synced, but not undoable. */
  addStressObjects(count: number, cards = false): void {
    const b = this.contentBounds();
    const objs = generateStressBoard(count, {
      seed: Date.now() & 0xffff,
      afterZ: this.store.topZ(),
      createdBy: this.userId,
      origin: b ? [b.x, b.y + b.h + 400] : [0, 0],
      cards,
    });
    this.applyLocal(objs.map((obj) => ({ t: 'create', obj })));
  }

  clearBoard(): void {
    this.commitTextEdit();
    this.applyLocal([...this.store.orderedIds()].map((id) => ({ t: 'delete', id })));
    this.history.clear();
    this.events.emit('history', undefined);
  }

  // --- internals ---

  private onStoreChange(ids: ReadonlySet<string>): void {
    let selectionChanged = false;
    let touchesSelection = false;
    // Connectors attached to anything that changed have new paths too.
    const dependents = this.connectors.update(ids);
    for (const id of ids) {
      const o = this.store.get(id);
      if (o) {
        this.index.update(id, this.boundsOf(o));
        if (o.type === 'frame') this.frameIds.add(id);
        else this.frameIds.delete(id);
        if (this._selection.has(id)) touchesSelection = true;
      } else {
        this.index.remove(id);
        this.frameIds.delete(id);
        this.layouts.forget(id);
        this.textCache.forget(id);
        if (this.audio.id === id) this.audio.stop();
        if (this._selection.delete(id)) selectionChanged = true;
        if (this.hovered === id) this.hovered = null;
      }
    }
    for (const id of dependents) {
      const o = this.store.get(id);
      if (o) this.index.update(id, this.boundsOf(o));
      if (this._selection.has(id)) touchesSelection = true;
    }
    this.renderer.objectsChanged(dependents.size ? new Set([...ids, ...dependents]) : ids);
    if (touchesSelection || selectionChanged || this.presence?.touches(ids)) this.renderer.invalidateInteractive();
    if (selectionChanged) this.events.emit('selection', undefined);
    if (touchesSelection) this.events.emit('selectionContent', undefined);
  }

  /** The selection (plus frame contents) as our clipboard format and as readable text. */
  private clipboardPayload(): { data: string; text: string } {
    const objs = withFrameChildren(this.store, this.selectedObjects());
    const readable = (o: BoardObject) => (o.type === 'card' ? cardPlainText(o) : plainText('text' in o ? o.text : undefined));
    return {
      data: JSON.stringify({ kind: CLIPBOARD_KIND, v: 1, objects: objs }),
      text: objs.map(readable).filter(Boolean).join('\n\n'),
    };
  }

  private ownCopy: { data: string; text: string } | null = null;

  /**
   * Browsers only let a page read standard types back from the clipboard, so
   * a menu's Paste can't see our objects there. Remember what this tab copied
   * (sessionStorage, so it survives moving to another board in the same tab).
   */
  private rememberCopy(copy: { data: string; text: string }): void {
    this.ownCopy = copy;
    try { sessionStorage.setItem(OWN_COPY_KEY, JSON.stringify(copy)); } catch { /* too large, or storage blocked: this page still has it */ }
  }

  private lastCopy(): { data: string; text: string } | null {
    try {
      const raw = sessionStorage.getItem(OWN_COPY_KEY);
      const v = raw ? JSON.parse(raw) as { data?: unknown; text?: unknown } : null;
      if (v && typeof v.data === 'string' && typeof v.text === 'string') return { data: v.data, text: v.text };
    } catch { /* fall back to memory */ }
    return this.ownCopy;
  }

  /** Copy or cut from a menu, through the same path as ⌘C / ⌘X. */
  copySelection(cut = false): void {
    if (!this._selection.size || (cut && this.readOnly)) return;
    if (document.execCommand(cut ? 'cut' : 'copy')) return;
    // No clipboard event (unsupported): keep our own copy, and give other apps the text.
    const copy = this.clipboardPayload();
    this.rememberCopy(copy);
    void navigator.clipboard?.writeText(copy.text).catch(() => {});
    if (cut) this.deleteSelection();
  }

  /**
   * Paste from a menu (there's no paste event): reads the clipboard where the
   * browser allows it. If it still holds the text this tab last copied, the
   * objects copied with it are pasted; otherwise images, then text.
   */
  async pasteFromClipboard(at: [number, number]): Promise<MenuPasteResult> {
    if (this.readOnly) return 'empty';
    let text: string | null = null; // null: the browser wouldn't let us read it
    const images: File[] = [];
    try {
      if (navigator.clipboard.read) {
        text = '';
        for (const item of await navigator.clipboard.read()) {
          const img = item.types.find((t) => t.startsWith('image/'));
          if (img) images.push(new File([await item.getType(img)], 'pasted-image', { type: img }));
          else if (item.types.includes('text/plain')) text = await (await item.getType('text/plain')).text();
        }
      } else {
        text = await navigator.clipboard.readText();
      }
    } catch {
      text = null;
    }
    const own = this.lastCopy();
    const ownObjs = own && !images.length && (text === null || text === own.text) ? parseClipboard(own.data) : null;
    if (ownObjs) {
      await this.paste(ownObjs, at);
      return 'pasted';
    }
    if (images.length && this.imageUploader) {
      this.imageUploader(images, at);
      return 'pasted';
    }
    if (text?.trim()) {
      const objs = parseClipboard(text);
      if (objs) await this.paste(objs, at);
      else this.pasteText(text.trim(), at);
      return 'pasted';
    }
    return text === null ? 'blocked' : 'empty';
  }

  /**
   * Right-click: what the menu acts on. An object keeps or becomes the
   * selection; inside a multi-selection keeps it; empty board clears it.
   * Returns whether there's a selection to act on.
   */
  selectForMenu(wx: number, wy: number): boolean {
    const hit = this.hitTest(wx, wy);
    if (hit) {
      if (!this._selection.has(hit.id)) this.setSelection([hit.id]);
      return true;
    }
    const bounds = union(this.selectedObjects().map((o) => this.boundsOf(o)));
    if (bounds && this._selection.size > 1 && wx >= bounds.x && wx <= bounds.x + bounds.w && wy >= bounds.y && wy <= bounds.y + bounds.h) return true;
    this.setSelection([]);
    return false;
  }

  private bindClipboard(): void {
    const active = () => !isEditableTarget(document.activeElement);
    const write = (data: DataTransfer) => {
      const copy = this.clipboardPayload();
      data.setData(CLIPBOARD_MIME, copy.data);
      data.setData('text/plain', copy.text);
      this.rememberCopy(copy);
    };

    document.addEventListener('copy', (e) => {
      if (!active() || !this._selection.size || !e.clipboardData) return;
      write(e.clipboardData);
      e.preventDefault();
    });

    document.addEventListener('cut', (e) => {
      if (!active() || !this._selection.size || !e.clipboardData || this.readOnly) return;
      write(e.clipboardData);
      e.preventDefault();
      this.deleteSelection();
    });

    document.addEventListener('paste', (e) => {
      if (!active() || this.readOnly || !e.clipboardData) return;
      const data = e.clipboardData;
      const images = [...data.files].filter((f) => f.type.startsWith('image/'));
      if (images.length && this.imageUploader) {
        e.preventDefault();
        this.imageUploader(images, this.pointer);
        return;
      }
      // Objects from this app (any board, any tab), then plain text from anywhere.
      const objs = parseClipboard(data.getData(CLIPBOARD_MIME)) ?? parseClipboard(data.getData('text/plain'));
      if (objs) {
        e.preventDefault();
        void this.paste(objs);
        return;
      }
      const text = data.getData('text/plain').trim();
      if (text) {
        e.preventDefault();
        this.pasteText(text);
      }
    });
  }

  /** Pastes centred on the pointer if it's over the board, else the viewport centre. */
  async paste(objs: BoardObject[], at?: [number, number]): Promise<void> {
    // Images and audio live on the server, so boards kept in the browser can't hold them.
    if (this.local) objs = objs.filter((o) => o.type !== 'image' && o.type !== 'audio');
    const bounds = union(objs.map(aabb));
    if (!bounds) return;
    await this.beforePaste?.(objs).catch(() => {});
    const view = this.viewport();
    const [px, py] = at ?? this.pointer ?? [view.x + view.w / 2, view.y + view.h / 2];
    const clones = this.cloneObjects(objs, px - (bounds.x + bounds.w / 2), py - (bounds.y + bounds.h / 2));
    this.setTool('select');
    this.apply(clones.map((obj) => ({ t: 'create', obj })));
    this.setSelection(clones.map((c) => c.id));
  }

  /** Plain text from another app becomes a text object. */
  private pasteText(text: string, at?: [number, number]): void {
    const view = this.viewport();
    const [px, py] = at ?? this.pointer ?? [view.x + view.w / 2, view.y + view.h / 2];
    const fontSize = Math.min(400, Math.max(4, Math.round(20 / this.camera.zoom)));
    const obj: TextObj = {
      id: newId(), type: 'text', x: px, y: py, w: fontSize * 18, h: fontSize,
      rotation: 0, z: this.zKeysOnTop(1)[0]!, createdBy: this.userId, fontSize,
      text: truncateRichText(fromPlainText(text)),
    };
    obj.h = layoutFor(obj, obj.text, canvasMeasure).height;
    this.setTool('select');
    this.apply([{ t: 'create', obj }]);
    this.setSelection([obj.id]);
  }
}

/** Points cloned frames/connectors at the clones rather than the originals where possible. */
function remapRefs(o: BoardObject, ids: Map<string, string>): BoardObject {
  if (o.type === 'frame') {
    return { ...o, children: o.children.flatMap((c) => (ids.has(c) ? [ids.get(c)!] : [])) };
  }
  if (o.type === 'connector') {
    const ep = (e: Endpoint): Endpoint =>
      'objectId' in e && ids.has(e.objectId) ? { ...e, objectId: ids.get(e.objectId)! } : e;
    return { ...o, from: ep(o.from), to: ep(o.to) };
  }
  return o;
}

/** Clipboard contents from this app, validated like anything from outside. */
function parseClipboard(text: string): BoardObject[] | null {
  if (!text.startsWith('{')) return null;
  try {
    const data = JSON.parse(text) as { kind?: unknown; objects?: unknown };
    if (data.kind !== CLIPBOARD_KIND || !Array.isArray(data.objects) || !data.objects.length) return null;
    return data.objects.every((o) => validateObject(o) === null) ? (data.objects as BoardObject[]) : null;
  } catch {
    return null;
  }
}
